class GiftWrap extends HTMLElement {
  constructor() {
    super();
    this.variantId = Number(this.dataset.variantId);
    this.checkbox = this.querySelector('.gift-wrap__checkbox');
    this.messageWrapper = this.querySelector('.gift-wrap__message');
    this.countEl = this.querySelector('.gift-wrap__count');
    this.textarea = this.querySelector('textarea');
    this.errorEl = this.querySelector('.gift-wrap__error');
    this.busy = false;

    this.checkbox.addEventListener('change', this.onToggle.bind(this));

    if (this.textarea) {
      this.textarea.addEventListener(
        'input',
        debounce(() => this.saveMessage(this.textarea.value), 400)
      );
    }
  }

  connectedCallback() {
    this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'gift-wrap') return;
      this.sync();
    });
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  // ---- helpers -----------------------------------------------------------

  fetchCart() {
    return fetch(`${routes.cart_url}.js`).then((response) => response.json());
  }

  // Total units in the cart that aren't the gift wrap itself.
  countWrappable(cart) {
    return cart.items.reduce(
      (total, item) => (item.variant_id === this.variantId ? total : total + item.quantity),
      0
    );
  }

  // Sets gift wrap to an absolute quantity. Idempotent, so repeated or
  // concurrent calls with the same value can't stack.
  async updateCart(quantity, extra = {}) {
    const config = fetchConfig();
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.body = JSON.stringify({
      updates: { [this.variantId]: quantity },
      sections: this.getSectionsToRender()
        .map((section) => section.name)
        .join(','),
      sections_url: window.location.pathname,
      ...extra,
    });

    const response = await fetch(routes.cart_update_url, config);
    const data = await response.json();
    if (data.status) throw new Error(data.description || data.message);
    return data;
  }

  setBusy(state) {
    this.busy = state;
    this.checkbox.disabled = state;
    this.classList.toggle('gift-wrap--loading', state);
  }

  paint(cart) {
    const present = cart.items.some((item) => item.variant_id === this.variantId);
    const wrappable = this.countWrappable(cart);

    this.checkbox.checked = present;
    if (this.messageWrapper) this.messageWrapper.hidden = !present;
    if (this.countEl) {
      this.countEl.innerHTML = `&times; ${wrappable}`;
      this.countEl.hidden = wrappable <= 1;
    }
    if (cart.item_count === 0) this.hidden = true;
  }

  // ---- section rendering -------------------------------------------------

  getSectionsToRender() {
    const sections = [];
    const items = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');
    const drawer = document.querySelector('cart-drawer');
    const bubble = document.getElementById('cart-icon-bubble');
    const live = document.getElementById('cart-live-region-text');

    if (items) {
      sections.push({
        name: items.dataset.id,
        selector: '.js-contents',
        target: items.querySelector('.js-contents'),
      });
    }
    if (footer) {
      sections.push({
        name: footer.dataset.id,
        selector: '.js-contents',
        target: footer.querySelector('.js-contents'),
      });
    }
    if (drawer) {
      sections.push({
        name: 'cart-drawer',
        selector: '.drawer__inner',
        target: drawer.querySelector('.drawer__inner'),
      });
    }
    if (bubble) {
      sections.push({ name: 'cart-icon-bubble', selector: '.shopify-section', target: bubble });
    }
    if (live) {
      sections.push({ name: 'cart-live-region-text', selector: '.shopify-section', target: live });
    }

    return sections.filter((section) => section.target);
  }

  renderSections(sections) {
    if (!sections) return;
    this.getSectionsToRender().forEach((section) => {
      const html = sections[section.name];
      if (!html) return;
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
      if (source) section.target.innerHTML = source.innerHTML;
    });
  }

  // ---- actions -----------------------------------------------------------

  async onToggle() {
    const adding = this.checkbox.checked;
    this.setBusy(true);
    this.errorEl.hidden = true;

    try {
      let quantity = 0;

      if (adding) {
        quantity = this.countWrappable(await this.fetchCart());
        if (quantity === 0) throw new Error('Add a product to your cart first.');
      }

      const data = await this.updateCart(quantity, adding ? {} : { attributes: { 'Gift message': '' } });

      const present = data.items.some((item) => item.variant_id === this.variantId);
      if (present !== adding) throw new Error('Could not update gift wrap. Please try again.');

      if (!adding && this.textarea) this.textarea.value = '';
      this.paint(data);
      this.renderSections(data.sections);
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-wrap', cartData: data });
    } catch (error) {
      this.checkbox.checked = !adding;
      this.errorEl.textContent = error.message || 'Could not update gift wrap. Please try again.';
      this.errorEl.hidden = false;
    } finally {
      this.setBusy(false);
    }
  }

  saveMessage(value) {
    const config = fetchConfig();
    config.body = JSON.stringify({ attributes: { 'Gift message': value } });
    return fetch(routes.cart_update_url, config);
  }

  // Runs whenever anything else changes the cart. Repairs a drifted
  // wrap quantity and keeps the checkbox honest.
  async sync() {
    if (this.busy) return;

    try {
      const cart = await this.fetchCart();
      const wrapLine = cart.items.find((item) => item.variant_id === this.variantId);
      const wrappable = this.countWrappable(cart);

      this.paint(cart);
      if (!wrapLine || wrapLine.quantity === wrappable) return;

      this.setBusy(true);
      const data = await this.updateCart(
        wrappable,
        wrappable === 0 ? { attributes: { 'Gift message': '' } } : {}
      );

      this.paint(data);
      this.renderSections(data.sections);
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-wrap', cartData: data });
    } catch (error) {
      // non-fatal — state is repaired on the next cart update or page load
    } finally {
      this.setBusy(false);
    }
  }
}

customElements.define('gift-wrap', GiftWrap);